import { inject, Injectable } from '@angular/core';
import { User } from 'firebase/auth';
import { Observable, from, map } from 'rxjs';
import { OpenAuthClient, injectOpenAuth } from '@open/ngx-firebase/auth';
import { ConfirmationResponse } from '@assets/responses/Confirmation.response';
import { LoginForUserWithPinRequest } from '@assets/requests/auth/LoginForUserWithPin.request';
import { UserModel } from '@models/user/User.model';
import { ProductContextService } from './product-context.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  readonly #client: OpenAuthClient<UserModel> = injectOpenAuth<UserModel>({
    appKey: 'mobileBenef',
    markActivatedOnPasswordUpdate: false,
    markActivatedOnPinLogin: true,
  });
  readonly #productContext: ProductContextService = inject(ProductContextService);

  readonly user$: Observable<User | null> = this.#client.user$;

  /** Purges the locally cached product context before signing out: it's keyed by `uid`, and the next beneficiary on this device must never inherit the previous one's dashboard variant. */
  logout(): Promise<void> {
    this.#productContext.forget();
    return this.#client.logout();
  }

  logout$(): Observable<void> {
    return from(this.logout());
  }

  login$(email: string, password: string): Observable<UserModel> {
    return this.#client.login$(email, password);
  }

  loginForUserWithPin$(request: LoginForUserWithPinRequest): Observable<void> {
    return this.#client.loginWithPin$(request).pipe(map(() => void 0));
  }

  checkIsActivatedUser$(email: string): Observable<{ isActivated: boolean }> {
    return this.#client.checkIsActivatedUser$(email);
  }

  createPinForUser$(email: string): Observable<ConfirmationResponse> {
    return this.#client.createPin$(email);
  }

  updateUserPassword$(password: string): Observable<UserModel> {
    return this.#client.updatePassword$(password);
  }

  acceptedCgu$(): Observable<void> {
    return this.#client.acceptCgu$();
  }
}
